import React, { useContext, useMemo, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import Cover1 from '@public/images/newAboutUs/Journey-1.png';
import Cover1Phone from '@public/images/newAboutUs/Journey-1-phone.png';
import Cover2 from '@public/images/newAboutUs/Journey-2.png';
import Cover2Phone from '@public/images/newAboutUs/Journey-2-phone.png';
import Cover3 from '@public/images/newAboutUs/Journey-3.png';
import Cover3Phone from '@public/images/newAboutUs/Journey-3-phone.png';
import Cover4 from '@public/images/newAboutUs/Journey-4.png';
import Cover4Phone from '@public/images/newAboutUs/Journey-4-phone.png';
import Cover5 from '@public/images/newAboutUs/Journey-5.png';
import Cover5Phone from '@public/images/newAboutUs/Journey-5-phone.png';
import Cover6 from '@public/images/newAboutUs/Journey-6.png';
import Cover6Phone from '@public/images/newAboutUs/Journey-6-phone.png';
import Cover7 from '@public/images/newAboutUs/Journey-7.png';
import Cover7Phone from '@public/images/newAboutUs/Journey-7-phone.png';
import Cover7ZhPhone from '@public/images/newAboutUs/Journey-7-phone-zh.png';
import Cover7Zh from '@public/images/newAboutUs/Journey-7-zh.png';
import Cover8 from '@public/images/newAboutUs/Journey-8.png';
import Cover8Phone from '@public/images/newAboutUs/Journey-8-phone.png';

import MaxAndMediumScreen from '@/components/Device/MaxAndMediumScreen';
import Mobile from '@/components/Device/Mobile';
import Title from '@/components/Title';

import { MyContext } from '@/config/context-manager';

import TabHeader from './Component/TabHeader';
import { tabsItemType } from './data.d';

const Journey = () => {
  const [activeTab, setActiveTab] = useState('2024');
  const { t } = useTranslation('aboutus');
  const { locale } = useContext(MyContext);

  const tabs: tabsItemType[] = useMemo(
    () => [
      {
        year: '2024',
        key: '2024',
        content: t('悟空历程-2024'),
        cover: Cover8,
        phoneCover: Cover8Phone,
      },
      {
        year: '2023',
        key: '2023',
        content: t('悟空历程-2023'),
        cover: Cover7,
        zhCover: Cover7Zh,
        phoneCover: Cover7Phone,
        phoneZhCover: Cover7ZhPhone,
      },
      {
        year: '2022',
        key: '2022',
        content: t('悟空历程-2022'),
        cover: Cover6,
        phoneCover: Cover6Phone,
      },
      {
        year: '2021',
        key: '2021',
        content: t('悟空历程-2021'),
        cover: Cover5,
        phoneCover: Cover5Phone,
      },
      {
        year: '2020',
        key: '2020',
        content: t('悟空历程-2020'),
        cover: Cover4,
        phoneCover: Cover4Phone,
      },
      {
        year: '2018',
        key: '2018',
        content: t('悟空历程-2018'),
        cover: Cover3,
        phoneCover: Cover3Phone,
      },
      {
        year: '2017',
        key: '2017',
        content: t('悟空历程-2017'),
        cover: Cover2,
        phoneCover: Cover2Phone,
      },
      {
        year: '2016',
        key: '2016',
        content: t('悟空历程-2016'),
        cover: Cover1,
        phoneCover: Cover1Phone,
      },
    ],
    [t],
  );

  const activeTabItem = useMemo(() => {
    return tabs.find((d) => d.key === activeTab);
  }, [activeTab, tabs]);

  const handleTabChange = (tab: tabsItemType) => {
    setActiveTab(tab.key);
  };

  return (
    <div className="mx-5 flex max-w-[984px] flex-col items-center bg-[url('/images/home/blog-bg.png')] bg-[length:375px_auto] bg-[center_42px] bg-no-repeat pt-20 lg:mx-auto lg:bg-[length:676px_auto] lg:bg-[center_32px] lg:pt-[164px] xl:max-w-[1400px] xl:bg-[length:800px_auto] xl:bg-[center_70px] xl:pt-[176px]">
      <Title
        className="relative z-20 mx-5 text-[28px] [&]:lg:leading-normal [&]:xl:text-[42px] [&]:xl:leading-normal"
        innerHTML
        theme="colorful"
        htmlLabel="h1"
      >
        {t('悟空历程-title')}
      </Title>

      <TabHeader tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />

      <div className="flex flex-col overflow-hidden rounded-3xl bg-white pb-6 shadow-[0px_8px_30px_0px_rgba(0,0,0,0.06)] lg:w-[850px] lg:flex-row-reverse lg:p-10 xl:w-[1164px] xl:p-12">
        <MaxAndMediumScreen>
          <Image
            src={locale === 'zh' ? activeTabItem?.zhCover || activeTabItem?.cover : activeTabItem?.cover}
            alt=""
            width={349}
            height={309}
            className="flex-shrink-0 lg:ml-10 lg:w-[348px] lg:rounded-3xl xl:ml-12 xl:w-[480px]"
          />
        </MaxAndMediumScreen>
        <Mobile>
          <Image
            src={locale === 'zh' ? activeTabItem?.phoneZhCover || activeTabItem?.phoneCover : activeTabItem?.phoneCover}
            alt=""
            width={335}
            height={262}
            className="w-full flex-shrink-0"
          />
        </Mobile>
        <div className="flex-1 px-4 pt-4 lg:p-0">
          <div className="relative pl-[17px] text-[24px] font-bold leading-normal after:absolute after:left-0 after:top-[6px] after:h-4 after:w-[5px] after:bg-gradient-to-b after:from-[#ff5353] after:via-[#6d38fb] after:to-[#009dff] after:content-[''] lg:pl-[30px] lg:text-[34px] lg:font-extrabold lg:after:top-[8px] lg:after:h-6 lg:after:w-[6px] xl:pl-7 xl:text-[40px] xl:font-extrabold xl:after:top-[12px] xl:after:w-2">
            {activeTabItem?.year}
          </div>
          <ul
            className="mx-5 mt-2 list-disc text-[16px] font-normal text-wk-words-2 lg:mr-0 lg:mt-3 lg:text-[14px] xl:mt-5 xl:text-[20px] [&_li]:mt-[6px] lg:[&_li]:mt-2"
            dangerouslySetInnerHTML={{ __html: activeTabItem?.content || '' }}
          />
        </div>
      </div>
    </div>
  );
};

export default Journey;
